<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">Employees</h4>
      <el-button v-if="canManageEmployees" type="primary" @click="$router.push({ name: 'employees-create' })">Create</el-button>
    </div>

    <card>
      <div class="d-flex flex-wrap align-items-center mb-3">
        <el-input
          v-model="filters.fullName"
          placeholder="Full name (RU)"
          clearable
          style="width: 240px"
          class="mr-3 mb-2"
          @input="onSearch"
        />
        <el-select
          v-model="filters.departmentId"
          placeholder="Department"
          clearable
          filterable
          style="width: 240px"
          class="mr-3 mb-2"
          @change="onSearch"
        >
          <el-option v-for="d in departments" :key="d.id" :label="d.name" :value="d.id" />
        </el-select>
        <el-input
          v-model="filters.email"
          placeholder="Email"
          clearable
          style="width: 220px"
          class="mr-3 mb-2"
          @input="onSearch"
        />
        <el-input
          v-model="filters.phone"
          placeholder="Personal phone"
          clearable
          style="width: 200px"
          class="mb-2"
          @input="onSearch"
        />
      </div>

      <el-table :data="items" v-loading="loading" :row-class-name="rowClass" style="width: 100%">
        <!-- users.id, the employee's id everywhere now; "—" for an old-admin row with no account -->
        <el-table-column label="#" width="100">
          <template slot-scope="{ row }">{{ row.id || "—" }}</template>
        </el-table-column>
        <el-table-column label="Image" width="100">
          <template slot-scope="{ row }">
            <div
              :class="['avatar-cell', { 'is-disabled': !row.id || !canManageEmployees }]"
              :title="row.id && canManageEmployees ? 'Change photo' : 'View only'"
              @click="row.id && canManageEmployees && openAvatar(row)"
            >
              <el-avatar v-if="row.avatar" :src="row.avatar" :size="56" shape="circle" />
              <el-avatar v-else :size="56" shape="circle" icon="el-icon-camera" />
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Full name (RU)" prop="full_name" min-width="220" />
        <el-table-column label="Department" min-width="200">
          <template slot-scope="{ row }">{{ row.department ? row.department.name : "—" }}</template>
        </el-table-column>
        <el-table-column label="Email" prop="email" min-width="220" />
        <el-table-column label="Personal phone" prop="phone_private" min-width="150" />
        <el-table-column label="Status" width="110">
          <template slot-scope="{ row }">
            <el-tag v-if="row.is_fired" type="danger" size="small">Fired</el-tag>
            <el-tag v-else-if="row.is_on_flex" size="small">On flex</el-tag>
            <el-tag v-else type="success" size="small">Active</el-tag>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="" width="150" align="center">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <el-button
                size="mini"
                type="primary"
                icon="el-icon-edit"
                :title="row.id ? (canManageEmployees ? 'Edit' : 'View') : noAccountHint"
                :disabled="!row.id"
                @click="$router.push({ name: 'employees-edit', params: { id: String(row.id) } })"
              />
              <!-- the site page, only for someone the site shows on its team page (role set) -->
              <el-button
                v-if="row.role && row.alias"
                size="mini"
                icon="el-icon-link"
                title="Open on the site"
                @click="openOnSite(row)"
              />
            </div>
          </template>
        </el-table-column>
      </el-table>

      <p v-if="!loading && !items.length" class="text-muted text-center mt-3 mb-0">
        Nothing found.
      </p>

      <div class="d-flex align-items-center mt-3">
        <el-pagination
          v-if="total > perPage"
          layout="prev, pager, next, total"
          :current-page="page"
          :page-size="perPage"
          :total="total"
          @current-change="goToPage"
        />
        <small class="text-muted ml-auto">
          <span class="legend legend-fired" /> fired
          <span class="legend legend-flex ml-3" /> on flex
        </small>
      </div>
    </card>

    <employee-avatar-dialog
      :visible.sync="avatarDialogVisible"
      :user-id="avatarRow ? avatarRow.id : null"
      :name="avatarRow ? avatarRow.full_name : ''"
      :current="avatarRow ? avatarRow.avatar : null"
      @changed="avatarRow.avatar = $event"
    />
  </div>
</template>

<script>
import employeeAccess from "@/components/Employees/employeeAccess";
import APIEmployees from "@/api/modules/employees";
import { resultErrorMessage, resultErrorOf } from "@/api/helpers/resultError";
import EmployeeAvatarDialog from "./EmployeeAvatarDialog";
import returnDomainLoc from "@/api/helpers/returnDomainLoc";

const emptyFilters = () => ({ fullName: "", email: "", phone: "", departmentId: "" });

export default {
  mixins: [employeeAccess],
  name: "EmployeesList",
  components: { EmployeeAvatarDialog },
  data() {
    return {
      items: [],
      departments: [],
      total: 0,
      page: 1,
      // the old admin's page size
      perPage: 20,
      filters: emptyFilters(),
      loading: false,
      searchTimer: null,
      avatarDialogVisible: false,
      avatarRow: null,
    };
  },
  computed: {
    noAccountHint() {
      return "No users account yet — save the employee once in the old admin";
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const response = await APIEmployees.getList({
          ...this.filters,
          departmentId: this.filters.departmentId || "",
          page: this.page,
          perPage: this.perPage,
        });
        if (this.refused(response)) return;
        const { data } = response;
        this.items = data.employees || [];
        this.total = (data.pagination && data.pagination.total_count) || 0;
      } catch (e) {
        this.notifyError(e, "Could not load the employees");
      } finally {
        this.loading = false;
      }
    },
    async loadDepartments() {
      try {
        const response = await APIEmployees.getDepartments();
        if (this.refused(response)) return;
        const { data } = response;
        this.departments = data.departments || [];
      } catch (e) {
        this.notifyError(e, "Could not load the departments");
      }
    },
    onSearch() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.page = 1;
        this.syncQuery();
        this.load();
      }, 300);
    },
    goToPage(page) {
      this.page = page;
      this.syncQuery();
      this.load();
    },
    // filters and page survive a reload and can be shared as a link
    syncQuery() {
      const query = { page: this.page > 1 ? String(this.page) : undefined };
      Object.entries(this.filters).forEach(([key, value]) => {
        query[key] = value === "" || value === null ? undefined : String(value);
      });
      this.$router.replace({ query }).catch(() => {});
    },
    readQuery() {
      const { query } = this.$route;
      this.page = Number(query.page) || 1;
      Object.keys(this.filters).forEach((key) => {
        if (query[key] !== undefined) this.filters[key] = query[key];
      });
      if (this.filters.departmentId !== "") this.filters.departmentId = Number(this.filters.departmentId);
    },
    // same colours as the old grid: red for fired, blue for on flex
    rowClass({ row }) {
      if (row.is_fired) return "employee-fired";
      if (row.is_on_flex) return "employee-flex";

      return "";
    },
    openOnSite(row) {
      window.open(`${returnDomainLoc("us-uk")}coordinator/${row.alias}/`, "_blank");
    },
    openAvatar(row) {
      if (!this.canManageEmployees) return;
      this.avatarRow = row;
      this.avatarDialogVisible = true;
    },
    // cms-api answers a refusal with HTTP 200 and a Result body
    refused(response) {
      const error = resultErrorOf(response);
      if (!error) return false;

      this.$notify({ type: "warning", title: resultErrorMessage(error) });

      return true;
    },
    notifyError(error, fallback) {
      const payload = error && error.response && error.response.data;
      if (payload && payload.error) {
        this.$notify({ type: "warning", title: resultErrorMessage(payload.error) });

        return;
      }
      this.$notify({ type: "warning", title: (error && error.message) || fallback });
    },
  },
  created() {
    this.$store.dispatch("employees/refreshPermissions");
    this.readQuery();
    this.loadDepartments();
    this.load();
  },
};
</script>

<style scoped>
.el-select {
  margin-bottom: 0;
}

/* one row, whatever the theme's button padding: Element's `+ .el-button` margin is what wrapped it */
.row-actions {
  display: inline-flex;
  flex-wrap: nowrap;
  gap: 6px;
}

.row-actions .el-button {
  margin-left: 0;
}

.avatar-cell {
  cursor: pointer;
  display: inline-block;
}

.avatar-cell.is-disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.legend {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 2px;
  vertical-align: middle;
}

.legend-fired {
  background: rgba(245, 108, 108, 0.25);
}

.legend-flex {
  background: rgba(64, 158, 255, 0.25);
}

::v-deep .employee-fired td {
  background: rgba(245, 108, 108, 0.12) !important;
}

::v-deep .employee-flex td {
  background: rgba(64, 158, 255, 0.12) !important;
}
</style>
